import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, CircularProgress } from "@mui/material";

import { usePageViewEventTracker } from "@utils/analytics";
import { colors } from "../../styles/constants";
import { Button } from "../../components/atoms/Button";
import { Header } from "../../components/atoms/Header";
import { FormField } from "../../components/molecules/FormField";
import { FormStack } from "../../components/molecules/FormStack";
import { SideImageTemplate } from "@templates/SideImageTemplate";

interface PaymentFailedPageProps {
  goBackUrl: string;
}

const useGoBack = (goBackUrl: string) => {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const goBack = React.useCallback(async () => {
    setBusy(true);
    try {
      await navigate(goBackUrl);
    } catch (err) {
      setError("Failed to navigate. Please try again later.");
    } finally {
      setBusy(false);
    }
  }, [setBusy, navigate, goBackUrl]);

  return { goBack, busy, error };
};

export const PaymentFailedPage: React.FC<PaymentFailedPageProps> = ({ goBackUrl }) => {
  usePageViewEventTracker("Payment failed");
  const { goBack, busy, error } = useGoBack(goBackUrl);

  return <PaymentFailedPageView busy={busy} error={error} goBack={goBack} />;
};

export type PaymentFailedPageViewProps = {
  busy?: boolean;
  error?: string | null;
  goBack: () => void;
};

export const PaymentFailedPageView: React.FC<PaymentFailedPageViewProps> = ({
  busy,
  error,
  goBack,
}) => {
  return (
    <SideImageTemplate headerType="help" busy={busy}>
      <Stack rowGap={4}>
        <Header level={1} color={colors.accentTeal}>
          Payment Failed
        </Header>
        <Typography variant="body1">
          There was an error with your payment. Please try again.
        </Typography>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
        <FormStack>
          <FormField twoThirdsWidth>
            <Button variant="primary" size="large" disabled={busy} onClick={goBack}>
              {busy ? <CircularProgress size={24} /> : "Go Back to Payment"}
            </Button>
          </FormField>
        </FormStack>
      </Stack>
    </SideImageTemplate>
  );
};
